import { useCallback, useEffect, useRef, useState } from 'react'
import { AppState, AppStateStatus } from 'react-native'
import { Analytics, CustomEventTypes } from '@src/config'
import { extractSpeechChunks } from '@src/screens/Nora/utils/speechText'
import { useNoraVoice } from '@src/screens/Nora/hooks/useNoraVoice'
import { useNoraTranscription } from '@src/screens/Nora/hooks/useNoraTranscription'
import type { UserTranscript } from '@src/screens/Nora/hooks/useNoraTranscription'
import { NoraAudioCapture } from '@src/screens/Nora/native/NoraAudioCapture'
import { useSnack } from '@src/utils/navigatorContext'
import { useNoraStreamChat } from '../context/NoraStreamChatContext'
import {
  useNoraVoiceModeContext,
  useNoraVoiceModeVisualActionsContext,
} from '../context/NoraVoiceModeContext'

const SILENCE_SEND_DELAY_MS = 2000

interface UseVoiceModeParams {
  conversationId?: string
}

// Bridge between Stream Chat and the two lower-level Nora voice hooks. The chat
// UI already receives Nora's answer as rich_text blocks, so this watches those
// blocks, turns them into stable speech chunks, and hands them to useNoraVoice
// (output). It also owns the mic/transcription lifecycle via useNoraTranscription
// (input), keeping speech output and user transcription independent of each
// other. Keeping this in a hook keeps voice-session code unaware of Stream
// message shapes and keeps the UI toggle unaware of Realtime details.
export const useVoiceMode = ({ conversationId }: UseVoiceModeParams = {}) => {
  const { channel, client } = useNoraStreamChat()
  const {
    enabled: voiceModeEnabled,
    muted: voiceModeMuted,
    source: voiceModeSource,
    transcript: voiceModeTranscript,
    setEndedSession: setVoiceModeEndedSession,
    setTranscript: setVoiceModeTranscript,
    setEnabled: setVoiceModeEnabled,
  } = useNoraVoiceModeContext()
  const {
    setSpeakingState: setVoiceModeSpeakingState,
    inputLevel: voiceModeInputLevel,
  } = useNoraVoiceModeVisualActionsContext()
  const showSnack = useSnack()
  const { userID } = client

  const {
    speak,
    stop: stopVoice,
    isSpeaking: noraIsSpeaking,
    currentAudioPath,
    onPlaybackEnd,
  } = useNoraVoice()
  const pendingTranscriptRef = useRef('')
  const silenceSendTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [userSpeaking, setUserSpeaking] = useState(false)
  const lifecycleGenerationRef = useRef(0)

  const reportVoiceStartupError = useCallback(() => {
    setVoiceModeEnabled(false)
    showSnack('Unable to start Voice Mode. Check microphone access and try again.', null, 'error')
  }, [setVoiceModeEnabled, showSnack])

  const clearSilenceSendTimer = useCallback(() => {
    if (silenceSendTimerRef.current) {
      clearTimeout(silenceSendTimerRef.current)
      silenceSendTimerRef.current = null
    }
  }, [])

  const clearPendingTranscript = useCallback(() => {
    pendingTranscriptRef.current = ''
    setVoiceModeTranscript('')
  }, [setVoiceModeTranscript])

  const sendPendingTranscript = useCallback(async () => {
    const text = pendingTranscriptRef.current.trim()
    if (!text) {
      clearPendingTranscript()
      return
    }

    clearSilenceSendTimer()
    try {
      await channel?.sendMessage({ text, skip_followup_questions: true })
      clearPendingTranscript()
    } catch {
      // Keep the transcript visible so the user does not lose dictated text on a
      // transient Stream failure.
    }
  }, [channel, clearPendingTranscript, clearSilenceSendTimer])

  const scheduleTranscriptSend = useCallback(() => {
    clearSilenceSendTimer()
    silenceSendTimerRef.current = setTimeout(() => {
      void sendPendingTranscript()
    }, SILENCE_SEND_DELAY_MS)
  }, [clearSilenceSendTimer, sendPendingTranscript])

  const handleTranscriptComplete = useCallback(
    ({ text }: UserTranscript) => {
      const trimmed = text.trim()
      if (!trimmed) {
        return
      }

      const nextTranscript = pendingTranscriptRef.current
        ? `${pendingTranscriptRef.current} ${trimmed}`
        : trimmed
      pendingTranscriptRef.current = nextTranscript
      setVoiceModeTranscript(nextTranscript)
      scheduleTranscriptSend()
    },
    [scheduleTranscriptSend, setVoiceModeTranscript],
  )

  const { start: startTranscription, stop: stopTranscription } = useNoraTranscription({
    shouldSuppressCapture: () => voiceModeMuted,
    onSpeechStart: () => {
      if (!voiceModeMuted) {
        setUserSpeaking(true)
        void stopVoice()
      }
    },
    onUtteranceReady: () => {
      setUserSpeaking(false)
    },
    onError: () => {
      setUserSpeaking(false)
      reportVoiceStartupError()
    },
    onTranscriptChange: (transcript) => setVoiceModeTranscript(transcript?.text ?? ''),
    onTranscriptComplete: handleTranscriptComplete,
    // Straight into the shared value: this fires ~20x/second and must not go
    // through React state, or every sample would re-render the chat tree.
    onLevel: (level) => {
      voiceModeInputLevel.value = level
    },
  })

  useEffect(() => {
    if (voiceModeMuted) {
      setUserSpeaking(false)
    }
  }, [voiceModeMuted])

  const handlePlaybackError = useCallback(
    (_error: Error) => {
      reportVoiceStartupError()
    },
    [reportVoiceStartupError],
  )

  const spokenTextByBlockIdRef = useRef<Record<string, string>>({})

  useEffect(() => {
    if (!voiceModeEnabled || !conversationId) {
      return
    }

    const eventProperties = {
      conversationId,
      ...(voiceModeSource ? { source: voiceModeSource } : {}),
    }
    const startedAtMs = Date.now()
    Analytics.track(CustomEventTypes.NoraVoiceModeStarted, eventProperties)

    return () => {
      const durationSeconds = Math.max(0, Math.round((Date.now() - startedAtMs) / 1000))
      Analytics.track(CustomEventTypes.NoraVoiceModeEnded, eventProperties)
      setVoiceModeEndedSession({
        id: `${conversationId}-${startedAtMs}`,
        conversationId,
        durationSeconds,
        source: voiceModeSource,
      })
    }
  }, [conversationId, setVoiceModeEndedSession, voiceModeEnabled, voiceModeSource])

  // Nora's OUTPUT speech: subscribe to Stream messages while Voice Mode is on
  // and turn assistant rich_text blocks into speech chunks. This effect owns
  // only the channel subscription/output side; INPUT (mic + transcription) and
  // the foreground lifecycle are handled by the separate effect below so that
  // output and input concerns can change independently.
  useEffect(() => {
    if (!voiceModeEnabled || !channel) {
      return
    }

    const handleMessage = ({ message }: { message?: any }) => {
      if (!message) {
        return
      }

      const messageUserId = message?.user?.id ?? message?.user_id

      if (messageUserId === userID) {
        void stopVoice()
        return
      }

      const chunks = extractSpeechChunks(message, spokenTextByBlockIdRef.current)

      if (chunks.length > 0) {
        speak(chunks)
      }
    }

    const subscriptions = [
      channel.on('message.new', handleMessage),
      channel.on('message.updated', handleMessage),
    ]

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe())
    }
  }, [channel, userID, speak, stopVoice, voiceModeEnabled])

  // Nora's INPUT lifecycle: start/stop the mic+transcription session as Voice
  // Mode is toggled, and enforce foreground-only operation via AppState so the
  // mic session tears down whenever the app is on background and a fresh
  // transcription secret/session is created when it returns to the foreground.
  /* eslint-disable react-hooks/exhaustive-deps -- voiceModeInputLevel is a
     stable shared-value ref, deliberately omitted from the dependency array:
     Reanimated 4 rejects a value that is both an effect dependency and written
     inside that effect. */
  useEffect(() => {
    if (!voiceModeEnabled) {
      return
    }

    const generation = lifecycleGenerationRef.current + 1
    lifecycleGenerationRef.current = generation

    const startSession = () => {
      if (lifecycleGenerationRef.current !== generation) {
        return
      }

      void startTranscription().catch(() => {
        if (lifecycleGenerationRef.current !== generation) {
          return
        }
        reportVoiceStartupError()
      })
    }

    // null/inactive are transitional states during foreground navigation. The
    // enable action itself is foreground intent, so only an explicit
    // background state suppresses initial startup.
    if (AppState.currentState !== 'background') {
      startSession()
    }

    const handleAppStateChange = (nextState: AppStateStatus) => {
      if (nextState === 'active') {
        startSession()
      } else {
        lifecycleGenerationRef.current += 1
        clearSilenceSendTimer()
        clearPendingTranscript()
        setUserSpeaking(false)
        voiceModeInputLevel.value = 0
        void stopVoice()
        void stopTranscription()
      }
    }

    const subscription = AppState.addEventListener('change', handleAppStateChange)

    return () => {
      lifecycleGenerationRef.current += 1
      subscription.remove()
      spokenTextByBlockIdRef.current = {}
      clearSilenceSendTimer()
      clearPendingTranscript()
      setUserSpeaking(false)
      // No further level events will arrive, so drop to silence rather than
      // leaving the animation stuck at the last sampled amplitude.
      voiceModeInputLevel.value = 0
      void stopVoice()
      void stopTranscription()
    }
  }, [
    clearPendingTranscript,
    clearSilenceSendTimer,
    startTranscription,
    stopTranscription,
    stopVoice,
    reportVoiceStartupError,
    voiceModeEnabled,
  ])
  /* eslint-enable react-hooks/exhaustive-deps */

  const isSpeaking = voiceModeEnabled && noraIsSpeaking

  // Publish speaking state so the Voice Mode background animation can subscribe
  // without creating its own useVoiceMode session.
  useEffect(() => {
    setVoiceModeSpeakingState({ isSpeaking, userSpeaking })
  }, [isSpeaking, userSpeaking, setVoiceModeSpeakingState])

  // Raise the native speech gate only while Nora's audio is actually playing. The strict
  // thresholds exist to stop her voice leaking past AEC from triggering a barge-in, but
  // they are far too deaf for normal conversation, so they must not persist into silence.
  useEffect(() => {
    void NoraAudioCapture.setEchoGuard(isSpeaking)
  }, [isSpeaking])

  return {
    userTranscript: voiceModeTranscript,
    userSpeaking,
    isSpeaking,
    currentAudioPath,
    onPlaybackEnd,
    onPlaybackError: handlePlaybackError,
  }
}
